import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Locale } from 'libs.i18n-core';
import { Button, I18nContextProvider, MessageResource, Spinner, Text, useLocalizeMessage } from 'libs.nucleus';
import lumosEnglishMessages from '../../../../../locales/lumos/en-US.json';
import { DocumentModal } from '../document_modal';
import { getConsentInviteErrorMessage } from './consent_invite.helpers';
import { useConsentInviteAccount } from './use_consent_invite_account.hook';
const lumosMessageProvider = () => new MessageResource(Locale.en_US, lumosEnglishMessages);
// Only `_id` is real — it's all DocumentModal needs to fetch the actual document itself.
// Every other field here is a placeholder to satisfy TDocument's shape; DocumentModal never
// reads `created`/`ec__status`/`ec__required_signers` directly, and shows `ec__document_template.ec__title`
// only until its own fetch resolves (a brief empty-title flash, not a lasting one).
const buildStubDocument = (inviteContext) => ({
    _id: inviteContext.invite.ec__signed_document,
    created: '',
    ec__document_template: {
        ec__title: '',
    },
    ec__required_signers: 0,
    ec__status: 'sent',
});
/**
 * Rendered inside {@link ConsentInviteView}'s own `I18nContextProvider` so its `translate()`
 * calls resolve against the lumos message set regardless of the host app's own i18n setup.
 */
const ConsentInviteViewInner = ({ inviteContext, isAuthLoading, isLoggedIn, userEmail, onNeedsLogin, onAccountReady, onSwitchAccount, onClose, }) => {
    const translate = useLocalizeMessage();
    const { account, error, setError, isLoading } = useConsentInviteAccount({
        inviteContext,
        isAuthLoading,
        isLoggedIn,
        userEmail,
        translate,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        if (inviteContext && !isAuthLoading && !isLoggedIn) {
            onNeedsLogin();
        }
    }, [inviteContext, isAuthLoading, isLoggedIn, onNeedsLogin]);
    useEffect(() => {
        if (account) {
            setIsModalOpen(true);
            onAccountReady?.(account);
        }
    }, [account, onAccountReady]);
    const handleSwitchAccount = async () => {
        setIsSubmitting(true);
        try {
            await onSwitchAccount();
        }
        catch (submitError) {
            setError(getConsentInviteErrorMessage(submitError, translate));
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        onClose();
    };
    if (isLoading || isAuthLoading || !isLoggedIn) {
        return _jsx(Spinner, { wrapper: 'full' });
    }
    if (account && inviteContext) {
        return (_jsxs("main", { className: 'flex min-h-screen items-center justify-center bg-neutral-50 p-6', children: [_jsx(Text, { children: translate('label.consent_invite.session_ready') }), _jsx(DocumentModal, { document: buildStubDocument(inviteContext), isOpen: isModalOpen, onClose: handleCloseModal, accountId: account._id, accountEmail: account.email, accountDisplayName: `${account.name.first} ${account.name.last}`.trim(), apiClient: inviteContext.accountApi, getApiErrorMessage: (apiError) => getConsentInviteErrorMessage(apiError, translate), isDocGenEconsentEnabled: true, canDownloadConsentPdf: true })] }));
    }
    return (_jsx("main", { className: 'flex min-h-screen items-center justify-center bg-neutral-50 p-6', children: _jsxs("section", { className: 'flex w-full max-w-md flex-col gap-6 rounded-lg bg-white p-8 shadow', children: [_jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { className: 'text-2xl font-semibold', children: translate('label.consent_invite.sign_your_document') }), _jsx(Text, { className: 'text-sm text-neutral-600', children: translate('label.consent_invite.use_invited_account') })] }), error && _jsx(Text, { className: 'text-sm text-red-600', children: error }), inviteContext && (_jsx(Button, { disabled: isSubmitting, label: isSubmitting
                        ? translate('label.consent_invite.please_wait')
                        : translate('label.consent_invite.sign_in_with_invited_account'), onClick: handleSwitchAccount, variant: 'primary' }))] }) }));
};
/**
 * Signer-facing view for a consent invite: resolves the invite/account, then renders
 * either the loading state, the sign-in prompt, or the signing modal. Host apps (e.g.
 * self_service's consent-invite route) only need to decode the invite token into a
 * {@link TConsentInviteContext}, supply auth state, and wire the three navigation
 * callbacks — no other invite-specific logic needs to live in the host app.
 */
export const ConsentInviteView = (props) => (_jsx(I18nContextProvider, { locale: Locale.en_US, messageProvider: lumosMessageProvider, children: _jsx(ConsentInviteViewInner, { ...props }) }));
